import React, { useCallback, useContext, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import API from '../../../../../../services/api'
import Banner from '../../../../../atoms/banner/Banner'
import Form from '../../../../../atoms/form/Form'
import VerticalGroup from '../../../../../atoms/verticalgroup/VerticalGroup'
import { EventContext } from '../../context'
import useForm from '../../../../../../hooks/useForm'
import Card from '../../../../../atoms/card/Card'
import Input from '../../../../../atoms/form/input/Input'
import InlineGroup from '../../../../../atoms/inlinegroup/InlineGroup'
import Datepicker from '../../../../../atoms/datepicker/Datepicker'
import ClientDropdown from '../../../../../organisms/client-dropdown/ClientDropdown'
import DisplayField from '../../../../../atoms/form/display-field/DisplayField'
import { convertToYYYYMMDD } from '../../../../../../lib/date'
import EventOptions from '../../components/EventOptions'
import StepContainer from '../../components/StepContainer'

const Details: React.FC<{}> = () => {
  const history = useHistory()
  const {
    config,
    updateConfig,
    assignOnSubmitFunction,
    updateFormStatus,
    formMode,
  } = useContext(EventContext)
  let [
    { formData, fieldErrors, error, inProgress, formUpdated },
    { handleSubmit, setField, setDateField, setDropdownField },
  ] = useForm(
    config?.name
      ? {
        ...config,
        date: new Date(config.date),
      }
      : {
        id: null,
        name: '',
        location: '',
        date: new Date(),
        clientId: null,
        participantsCount: null,
      }
  )

  const {
    id,
    name,
    date,
    clientId,
    streamName,
    location,
    participantsCount,
  } = formData

  const onSubmit = useCallback(async () => {
    const payload: API.EventRequest = {
      name: name.trim(),
      clientId,
      date: convertToYYYYMMDD(date),
      location,
      streamName,
    }
    if (formMode === 'create') {
      // This will be a create event form if no `config.id` present
      const response = await API.createEvent(payload)
      history.push(`/events/${response.id}/view-wizard?stepKey=branding`)
    } else if (formMode === 'edit' && config?.id) {
      const response = await API.editEvent(config.id, payload)
      updateConfig(response)
      return response
    } else if (formMode === 'clone' && config) {
      // This is a clone event
      const response = await API.duplicateEvent(config.id, {
        ...payload,
        brandingId: config.brandingId,
        schedule: config.schedule,
        simulationId: config.simulationId,
        assessmentId: config.assessmentId,
        reportingTemplateId: config.reportingTemplateId,
      })
      history.push(`/events/${response.id}/view-wizard?stepKey=branding`)
    }
  }, [
    clientId,
    config,
    date,
    formMode,
    history,
    location,
    name,
    streamName,
    updateConfig,
  ])

  const validate = useCallback(() => {
    const errors = []
    if (!clientId) {
      errors.push({ field: 'clientId', message: 'Select a client' })
    }
    if (!name) {
      errors.push({ field: 'name', message: 'Enter an event name' })
    }
    if (!location) {
      errors.push({ field: 'location', message: 'Enter a location' })
    }
    return errors
  }, [clientId, location, name])

  useEffect(() => {
    assignOnSubmitFunction(handleSubmit(onSubmit, validate))
  }, [assignOnSubmitFunction, handleSubmit, onSubmit, validate])

  useEffect(() => {
    updateFormStatus({
      inProgress: inProgress,
      formUpdated: formUpdated,
    })
  }, [updateFormStatus, inProgress, formUpdated])

  const onClientChange = useCallback(
    ({ label, value }: { label: string, value: string }) => {
      setDropdownField('clientId')({ label, value })
    },
    [setDropdownField]
  )

  return (
    <StepContainer>
      <Banner type="error" active={!!error} message={error?.message} />
      <InlineGroup spread verticalCenter block>
        <h2>Details</h2>
        <EventOptions />
      </InlineGroup>
      <Form wide>
        <Card wide padding={16}>
          <InlineGroup spread evenWidthChildren>
            <VerticalGroup center spaceBetweenElements={4}>
              <Datepicker
                isVerticalLayout
                label="Date *"
                date={date}
                onChange={setDateField('date')}
                error={fieldErrors.date}
              />
              <ClientDropdown
                isVerticalLayout
                label="Client *"
                clientId={clientId}
                onChange={onClientChange}
                error={fieldErrors.clientId}
              />
              <Input
                isVerticalLayout
                data-test="location"
                type="text"
                label="Location *"
                value={location}
                onChange={setField('location')}
                error={fieldErrors.location}
              />
            </VerticalGroup>
            <VerticalGroup center spaceBetweenElements={4}>
              <Input
                isVerticalLayout
                data-test="name"
                type="text"
                label="Event Name *"
                value={name}
                onChange={setField('name')}
                error={fieldErrors.name}
              />
              <Input
                isVerticalLayout
                data-test="streamName"
                type="text"
                label="Stream Name"
                value={streamName || ''}
                onChange={setField('streamName')}
                error={fieldErrors.streamName}
              />
            </VerticalGroup>
          </InlineGroup>
          {id ? (
            <>
              <hr className="mt-12" />
              <InlineGroup spread evenWidthChildren className="pt-4">
                <VerticalGroup center spaceBetweenElements={4}>
                  <DisplayField
                    compact
                    isVerticalLayout
                    label="Event ID"
                    value={id}
                  />
                </VerticalGroup>
                <VerticalGroup center spaceBetweenElements={4}>
                  <DisplayField
                    compact
                    isVerticalLayout
                    label="Participants"
                    value={participantsCount}
                  />
                </VerticalGroup>
              </InlineGroup>
            </>
          ) : null}
        </Card>
      </Form>
    </StepContainer>
  )
}

export default Details
